// AI Chat Component
import { getEditor } from './editor.js';
import { getCurrentFile, getCurrentCRD } from './files.js';
import { apiFetch, apiPost } from './api.js';

let chatHistory = [];
let isStreaming = false;
let isMinimized = false;
let currentCrdFolder = null;
let currentCrdFilename = null;

function getRobotIconHTML() {
  return `
    <div class="ai-robot-header" style="display: flex; justify-content: center; align-items: center; padding: 24px 0 16px 0;">
      <div style="font-size: 20px; color: #141414; width: 48px; height: 48px; background: #f5f5f5; border-radius: 50%; display: flex; align-items: center; justify-content: center;">
        <i class="ph ph-robot"></i>
      </div>
    </div>
  `;
}

function getClearButtonHTML() {
  return `
    <div class="clear-chat-container">
      <button id="aiClearBtn" class="clear-chat-btn">Clear chat</button>
    </div>
  `;
}

async function handleCreateWorkItemsButton() {
  const currentCRD = await getCurrentCRD();

  if (!currentCRD || !currentCRD.folder || !currentCRD.filename) {
    alert('Please open a CRD file first to create work items.');
    return;
  }

  // Extract component name from filename (e.g., "Card_CRD.md" -> "Card")
  const componentName = currentCRD.filename.replace('_CRD.md', '').replace('_v\\d+', '');

  // Create the prompt with all context
  const prompt = `Create Azure DevOps work items for the ${componentName} component based on the current CRD.

CRITICAL: The full content of output-rules.md and component-feature-creation-guide.md are included in your system context as <ds_document> tags. You MUST use the COMPLETE content from these documents, not summaries or paraphrases.

1. **Output Rules** (from output-rules.md - FULL DOCUMENT AVAILABLE):
   - Read the complete output-rules.md document from your system context
   - Use British English spelling throughout (colour, organise, behaviour, realise, analyse, etc.)
   - Use sentence case for all titles (capitalise only first word and proper nouns)
   - Use Oxford commas in lists
   - Use single quotes for emphasis
   - Don't use em dashes
   - Follow ALL rules from the complete document

2. **Component Feature Creation Guide** (from component-feature-creation-guide.md - FULL DOCUMENT AVAILABLE):
   - Read the complete component-feature-creation-guide.md document from your system context
   - Follow the EXACT structure for Feature and Stories as specified in the guide
   - Use the COMPLETE standard acceptance criteria templates (copy them exactly, then customise with component-specific details)
   - Include ALL required sections: Description, Objectives, Scope (In scope/Out of scope), Dependencies, Open Questions
   - Create exactly 3 stories: Design, Develop, Document
   - Use the COMPLETE task lists from the guide, customising based on the CRD content
   - Follow the exact naming conventions, story structure, and acceptance criteria format from the guide

3. **Format Requirements:**
   - Feature: Component: ${componentName}
   - Title: Component: ${componentName}
   - Include Labels: epic, component, feature (these become Azure Boards tags)
   - Story 1: Design ${componentName.toLowerCase()} component
   - Story 2: Develop ${componentName.toLowerCase()} component
   - Story 3: Document ${componentName.toLowerCase()} component
   - Each story must include: Title, Labels, Parent Epic, Description, Acceptance Criteria, Tasks (where applicable)

4. **Content Requirements:**
   - Extract all details from the current CRD
   - Use the CRD's variants, states, sizes, and requirements
   - Include all open questions from the CRD
   - Reference the CRD as the specification source
   - Ensure all content follows British English spelling and sentence case titles

Generate the complete work items following the component-feature-creation-guide.md structure EXACTLY, using the full templates and acceptance criteria from that document.`;

  // Set the input value and send
  const aiInput = document.getElementById('aiInput');
  aiInput.value = prompt;
  
  // Trigger send
  await handleSendMessage();
}

export function initAI() {
  console.log('🤖 Initializing AI Chat...');
  
  // Get references to chat elements
  const aiChatContainer = document.getElementById('aiChat');
  const aiInput = document.getElementById('aiInput');
  const aiSendBtn = document.getElementById('aiSend');
  const aiCreateIssuesBtn = document.getElementById('aiCreateIssuesBtn');
  
  // Setup event listeners
  aiSendBtn.addEventListener('click', handleSendMessage);
  aiCreateIssuesBtn.addEventListener('click', handleCreateWorkItemsButton);
  aiInput.addEventListener('keypress', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  });
  
  // Add event delegation for insert apply buttons
  aiChatContainer.addEventListener('click', async (e) => {
    // Handle undo link clicks
    if (e.target.classList.contains('undo-link')) {
      const button = e.target.closest('.insert-apply-btn');
      if (!button) return;
      
      // Get stored data from button
      const insertStart = parseInt(button.dataset.insertStart);
      const insertEnd = parseInt(button.dataset.insertEnd);
      const originalContent = button.dataset.originalContent;
      const action = button.dataset.insertAction;
      
      console.log('↩️ Undo clicked');
      console.log('📍 Stored range:', insertStart, 'to', insertEnd);
      console.log('🎬 Original action:', action);
      
      const editor = getEditor();
      if (editor && insertStart && insertEnd) {
        editor.commands.focus();
        
        if (action === 'replace') {
          // For replace: delete what we inserted, restore what was there
          console.log('🗑️ Deleting inserted content from', insertStart, 'to', insertEnd);
          
          console.log('🔍 DEBUG UNDO - About to delete from', insertStart, 'to', insertEnd);
          const beforeDelete = editor.state.doc.slice(insertStart, insertEnd);
          console.log('🔍 DEBUG UNDO - Content being deleted:', beforeDelete.content.textBetween(0, beforeDelete.content.size, ' '));
          console.log('🔍 DEBUG UNDO - Full doc size before delete:', editor.state.doc.content.size);
          
          editor.commands.deleteRange({ from: insertStart, to: insertEnd });
          
          console.log('🔍 DEBUG UNDO - Doc size after delete:', editor.state.doc.content.size);
          
          // Restore original content
          const contentArray = JSON.parse(originalContent);
          // insertContentAt expects an array of nodes or a fragment
          editor.commands.insertContentAt(insertStart, contentArray);
          console.log('♻️ Restored original content, nodes:', contentArray.length);
        } else {
          // For append: just delete what we inserted
          console.log('🗑️ Deleting appended content from', insertStart, 'to', insertEnd);
          
          console.log('🔍 DEBUG UNDO - About to delete from', insertStart, 'to', insertEnd);
          const beforeDelete = editor.state.doc.slice(insertStart, insertEnd);
          console.log('🔍 DEBUG UNDO - Content being deleted:', beforeDelete.content.textBetween(0, beforeDelete.content.size, ' '));
          console.log('🔍 DEBUG UNDO - Full doc size before delete:', editor.state.doc.content.size);
          
          editor.commands.deleteRange({ from: insertStart, to: insertEnd });
          
          console.log('🔍 DEBUG UNDO - Doc size after delete:', editor.state.doc.content.size);
        }
      }
      
      // Reset button to original state - clear ALL state
      button.textContent = button.dataset.label;
      button.classList.remove('applied');
      button.dataset.state = 'ready';  // Reset state so click handler works again
      delete button.dataset.insertStart;
      delete button.dataset.insertEnd;
      delete button.dataset.insertAction;
      delete button.dataset.originalContent;

      // Clear any inline styles that might have been set
      button.style.background = '';
      button.style.borderColor = '';
      button.style.color = '';

      console.log('↩️ Button reset to ready state');
      return;
    }
    
    if (e.target.classList.contains('insert-apply-btn')) {
      console.log('🔘 Insert button clicked');
      const button = e.target;
      
      // Prevent multiple clicks
      if (button.disabled) {
        console.log('⏸️ Button already disabled, ignoring click');
        return;
      }
      
      // Skip if already applied
      if (button.dataset.state === 'applied') {
        console.log('⏸️ Already applied, ignoring click');
        return;
      }
      
      const action = button.dataset.action;
      const section = button.dataset.section;
      const content = decodeURIComponent(button.dataset.content);
      const label = button.dataset.label;
      
      console.log('📋 Section to find:', section);
      console.log('🎬 Action:', action);
      
      button.disabled = true;
      button.textContent = 'Inserting...';
      
      try {
        const result = await insertContentIntoEditor(action, section, content);
        
        if (result.success) {
          console.log('✅ Content inserted successfully:', result.start, 'to', result.end);
          
          // Highlight the inserted content
          highlightInsertedContent(result.start, result.end);
          
          // Update button to show applied state with undo option
          button.innerHTML = '<span>✓ Applied</span> | <span class="undo-link">Undo</span>';
          button.dataset.state = 'applied';
          button.dataset.insertStart = result.start;
          button.dataset.insertEnd = result.end;
          button.dataset.insertAction = result.action;
          if (result.originalContent) {
            button.dataset.originalContent = result.originalContent;
          }
          button.disabled = false;
          button.style.background = '';
        } else {
          console.error('❌ Insert failed:', result.error);
          button.textContent = '✗ Failed';
          button.style.background = '#ef4444';
          setTimeout(() => {
            button.disabled = false;
            button.textContent = label;
            button.style.background = '';
          }, 2000);
        }
      } catch (error) {
        console.error('💥 Error inserting content:', error);
        button.textContent = '✗ Error';
        button.style.background = '#ef4444';
        setTimeout(() => {
          button.disabled = false;
          button.textContent = label;
          button.style.background = '';
        }, 2000);
      }
    }
  });
  
  // Add clear chat button
  addClearChatButton();
  
  // Initialize resizable layout
  initResizableLayout();
  
  // Load general chat history (no CRD open yet)
  loadChatHistory();
  
  console.log('✅ AI Chat initialized');
}

function addClearChatButton() {
  const aiChatContainer = document.getElementById('aiChat');
  
  if (!aiChatContainer) {
    console.error('❌ AI chat container not found!');
    return;
  }
  
  // Use event delegation - listen on container, check if click was on clear button
  aiChatContainer.addEventListener('click', (e) => {
    const clearBtn = e.target.closest('#aiClearBtn');
    if (clearBtn) {
      console.log('🖱️ Clear button clicked!');
      handleClearChat();
    }
  });
  
  console.log('✅ Clear button event delegation attached to container');
}

function toggleMinimize() {
  const aiSidebar = document.querySelector('.ai-sidebar');
  const minimizeBtn = document.getElementById('aiMinimizeBtn');
  
  isMinimized = !isMinimized;
  
  if (isMinimized) {
    aiSidebar.style.width = '40px';
    minimizeBtn.innerHTML = '<i class="ph ph-arrows-out-simple"></i>';
    // Hide chat content
    document.getElementById('aiChat').style.display = 'none';
    document.querySelector('.ai-input-area').style.display = 'none';
  } else {
    aiSidebar.style.width = '320px';
    minimizeBtn.innerHTML = '<i class="ph ph-arrows-in-simple"></i>';
    // Show chat content
    document.getElementById('aiChat').style.display = 'flex';
    document.querySelector('.ai-input-area').style.display = 'flex';
  }
}

export function updateAIChatContext(folder = null, filename = null) {
  console.log('🔄 Updating AI chat context:', { folder, filename });
  
  currentCrdFolder = folder;
  currentCrdFilename = filename;
  
  // Update all INSERT buttons to reflect the new current CRD
  updateAllInsertButtons();
  
  // Don't clear or reload - chat persists across CRD switches
  console.log('📌 Chat context updated, INSERT buttons refreshed');
}

function loadChatHistory() {
  // Load from global storage (not per-CRD)
  const key = 'chat_history_global';
  const saved = localStorage.getItem(key);
  
  if (saved) {
    chatHistory = JSON.parse(saved);
    renderChatHistory();
  } else {
    chatHistory = [];
    renderWelcomeMessage();
  }
}

function saveChatHistory() {
  // Always save to global storage
  const key = 'chat_history_global';
  localStorage.setItem(key, JSON.stringify(chatHistory));
}

function renderWelcomeMessage() {
  const aiChatContainer = document.getElementById('aiChat');
  if (!aiChatContainer) return;
  
  const welcomeHTML = `
    ${getRobotIconHTML()}
    <div class="ai-message assistant" style="padding: 16px 0; margin: 0; color: #141414; font-size: 14px; line-height: 1.5;">
      Hi! I'm here to help you write and improve CRDs. Ask me anything about design systems, accessibility, or component requirements.
    </div>
    ${getClearButtonHTML()}
  `;
  
  aiChatContainer.innerHTML = welcomeHTML;
  aiChatContainer.scrollTop = 0;
}

async function renderChatHistory() {
  const aiChatContainer = document.getElementById('aiChat');
  
  // Start with robot icon
  aiChatContainer.innerHTML = getRobotIconHTML();
  
  // Then add all messages
  for (const [index, msg] of chatHistory.entries()) {
    const msgEl = await createMessageElement(msg, index);
    aiChatContainer.appendChild(msgEl);
  }
  
  // Add clear button at the end
  const clearBtnDiv = document.createElement('div');
  clearBtnDiv.innerHTML = getClearButtonHTML();
  aiChatContainer.appendChild(clearBtnDiv.firstElementChild);
  
  scrollToBottom();
}

function renderInsertBlock(insertData, targetCrd = null) {
  const { action, section, label, content } = insertData;
  
  // Generate a unique ID for this button
  const buttonId = `insert-btn-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  // Use passed-in target CRD (from stored message) or fall back to current state
  const targetFolder = targetCrd?.folder || currentCrdFolder || '';
  const targetFilename = targetCrd?.filename || currentCrdFilename || '';
  
  // Render the markdown content
  const renderedContent = marked.parse(content);
  
  // Create the insert block as a DOM element
  const blockDiv = document.createElement('div');
  blockDiv.className = 'insert-block';
  // Store target CRD on the block itself for later reference
  blockDiv.dataset.targetFolder = targetFolder;
  blockDiv.dataset.targetFilename = targetFilename;
  
  // Create context header showing target CRD (from stored values)
  const contextHeader = document.createElement('div');
  contextHeader.className = 'insert-context';
  if (targetFilename) {
    contextHeader.innerHTML = `<span class="context-label">For:</span> <span class="context-filename">${targetFilename}</span>`;
  } else {
    contextHeader.innerHTML = `<span class="context-label">No CRD was open when created</span>`;
  }
  
  // Create content div
  const contentDiv = document.createElement('div');
  contentDiv.className = 'insert-content markdown-content';
  contentDiv.innerHTML = renderedContent;
  
  // Create button
  const button = document.createElement('button');
  button.className = 'insert-apply-btn';
  button.id = buttonId;
  button.dataset.action = action;
  button.dataset.section = section;
  button.dataset.content = encodeURIComponent(content);
  button.dataset.label = label;
  button.dataset.targetFolder = targetFolder;
  button.dataset.targetFilename = targetFilename;
  
  // Set initial button state
  updateInsertButtonState(button);
  
  // Assemble the block
  blockDiv.appendChild(contextHeader);
  contentDiv.appendChild(button);
  blockDiv.appendChild(contentDiv);
  
  return blockDiv;
}

async function renderWorkItemButton(issueContent, targetCrd = null) {
  // Get current CRD path for linking
  const targetFolder = targetCrd?.folder || currentCrdFolder || '';
  const targetFilename = targetCrd?.filename || currentCrdFilename || '';
  
  // Build CRD path if we have both folder and filename
  let crdPath = null;
  if (targetFolder && targetFilename) {
    // Get config for docsPath
    let config = null;
    try {
      const res = await fetch('/api/config');
      config = await res.json();
    } catch (error) {
      console.error('Failed to load GitHub config:', error);
      config = { docsPath: 'docs/crds' }; // Fallback
    }
    crdPath = `${config.docsPath}/${targetFolder}/${targetFilename}`;
  }
  
  // Create button container
  const buttonContainer = document.createElement('div');
  buttonContainer.className = 'github-issue-block';
  buttonContainer.style.cssText = `
    margin-top: 16px;
    padding: 12px;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
  `;
  
  // Context header
  const contextHeader = document.createElement('div');
  contextHeader.className = 'issue-context';
  contextHeader.style.cssText = 'margin-bottom: 8px; font-size: 12px; color: #6b7280;';
  if (targetFilename) {
    contextHeader.innerHTML = `<span>📋 Azure work items for: <strong>${targetFilename}</strong></span>`;
  } else {
    contextHeader.innerHTML = `<span>📋 Azure work items detected</span>`;
  }
  
  // Button
  const button = document.createElement('button');
  button.className = 'github-issue-btn';
  button.style.cssText = `
    width: 100%;
    padding: 10px 16px;
    background: #ec4899;
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s;
  `;
  button.textContent = 'Create Azure work items';
  button.dataset.issueContent = encodeURIComponent(issueContent);
  if (crdPath) {
    button.dataset.crdPath = crdPath;
  }
  
  button.addEventListener('mouseenter', () => {
    button.style.background = '#db2777';
  });
  button.addEventListener('mouseleave', () => {
    button.style.background = '#ec4899';
  });
  
  button.addEventListener('click', async () => {
    await handleCreateWorkItems(button, issueContent, crdPath);
  });
  
  buttonContainer.appendChild(contextHeader);
  buttonContainer.appendChild(button);
  
  return buttonContainer;
}

async function handleCreateWorkItems(button, issueContent, crdPath) {
  // Show confirmation dialog
  const confirmed = confirm(
    'This will create Azure DevOps work items for:\n' +
    '- Feature\n' +
    '- Design story\n' +
    '- Development story\n' +
    '- Documentation story\n\n' +
    'Continue?'
  );

  if (!confirmed) return;

  button.disabled = true;
  button.textContent = 'Creating work items...';
  button.style.background = '#9ca3af';
  button.style.cursor = 'not-allowed';

  try {
    const response = await apiPost('/api/azure/workitems/create', {
      markdown: issueContent,
      crdPath: crdPath
    });
    
    const contentType = response.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    let result;
    
    const text = await response.text();
    if (isJson) {
      try {
        result = text ? JSON.parse(text) : null;
      } catch (parseErr) {
        throw new Error(`Invalid server response: ${text.substring(0, 100)}${text.length > 100 ? '...' : ''}`);
      }
    } else {
      result = null;
    }
    
    if (!response.ok) {
      throw new Error(
        (result && result.message) || 
        (result && result.error) || 
        text.substring(0, 200) ||
        'Failed to create work items'
      );
    }
    
    if (!result) {
      throw new Error(text || 'Empty or invalid server response');
    }
    
    if (result.errors && result.errors.length > 0) {
      console.error('Some work items failed to create:', result.errors);
      button.textContent = `⚠️ ${result.totalCreated}/${result.totalCreated + result.totalFailed} created`;
      button.style.background = '#f59e0b';

      // Show error details
      setTimeout(() => {
        alert(
          `Created ${result.totalCreated} work items.\n\n` +
          `Failed: ${result.totalFailed}\n\n` +
          `Check console for details.`
        );
      }, 500);
    } else {
      button.textContent = '✓ Work items created';
      button.style.background = '#10b981';

      // Show success message with links
      const workItemLinks = result.created.map(workItem =>
        `#${workItem.id}: ${workItem.title}`
      ).join('\n');

      setTimeout(() => {
        const openLinks = confirm(
          `Successfully created ${result.totalCreated} work items!\n\n` +
          workItemLinks + '\n\n' +
          'Open work items in Azure DevOps?'
        );

        if (openLinks) {
          result.created.forEach(workItem => {
            window.open(workItem.url, '_blank');
          });
        }
      }, 500);
    }

    // Reset button after 5 seconds
    setTimeout(() => {
      button.disabled = false;
      button.textContent = 'Create Azure work items';
      button.style.background = '#ec4899';
      button.style.cursor = 'pointer';
    }, 5000);

  } catch (error) {
    console.error('Error creating Azure work items:', error);
    button.textContent = '✗ Failed';
    button.style.background = '#ef4444';

    setTimeout(() => {
      button.disabled = false;
      button.textContent = 'Create Azure work items';
      button.style.background = '#ec4899';
      button.style.cursor = 'pointer';
    }, 3000);

    alert(`Failed to create Azure work items: ${error.message}`);
  }
}

async function updateInsertButtonState(button) {
  // Skip if button is already applied
  if (button.dataset.state === 'applied') {
    return;
  }
  
  const targetFolder = button.dataset.targetFolder || '';
  const targetFilename = button.dataset.targetFilename || '';
  const label = button.dataset.label || 'Apply';
  const currentCrd = await getCurrentCRD();
  console.log('🔍 Comparing:', { targetFolder, targetFilename, currentFolder: currentCrd?.folder, currentFilename: currentCrd?.filename });
  
  // Reset any inline styles first
  button.style.background = '';
  button.style.borderColor = '';
  button.style.color = '';
  button.style.opacity = '';
  button.style.cursor = '';
  
  if (!targetFilename) {
    // INSERT was created with no CRD open - permanently disabled
    button.disabled = true;
    button.textContent = 'No target CRD specified';
  } else if (!currentCrd) {
    // No CRD is open - disabled, tell user which to open
    button.disabled = true;
    button.textContent = `Open ${targetFilename} to apply`;
  } else if (currentCrd.folder === targetFolder && 
             currentCrd.filename === (targetFilename.endsWith('.md') ? targetFilename : targetFilename + '.md')) {
    // Correct CRD is open - enabled!
    button.disabled = false;
    button.textContent = label;
  } else {
    // Different CRD is open - disabled, tell user which to open
    button.disabled = true;
    button.textContent = `Open ${targetFilename} to apply`;
  }
}

export function updateAllInsertButtons() {
  const buttons = document.querySelectorAll('.insert-apply-btn');
  console.log('🔄 Updating', buttons.length, 'INSERT buttons');
  buttons.forEach(button => {
    updateInsertButtonState(button);
  });
}

function findSectionInEditor(sectionPath) {
  if (window.DEBUG_SECTION_FINDING) {
    console.log('🐛 [DEBUG] Starting findSectionInEditor with path:', sectionPath);
  }
  
  console.log('🔍 Finding section:', sectionPath);
  const editor = getEditor();
  if (!editor) {
    console.error('❌ Editor not available');
    return { found: false, error: 'Editor not initialized' };
  }
  
  console.log('✅ Editor instance found');
  
  // Parse the section path (e.g., "Requirements > Accessibility" or "Scope")
  const parts = sectionPath.split('>').map(p => p.trim());
  console.log('📝 Section parts:', parts);
  
  if (window.DEBUG_SECTION_FINDING) {
    console.log('🐛 [DEBUG] Parsed path into', parts.length, 'parts:', parts);
  }
  
  // Get editor content as HTML
  const html = editor.getHTML();
  console.log('📄 Editor HTML length:', html.length);
  
  // Create a temporary DOM to search through
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  
  // Get all headings (needed for both simple and nested paths)
  const headings = tempDiv.querySelectorAll('h1, h2, h3, h4, h5, h6');
  console.log('📑 Total headings found:', headings.length);
  
  let targetHeading = null;
  
  if (parts.length === 1) {
    // Simple path - find direct heading
    console.log('🔎 Searching for simple heading:', parts[0]);
    
    if (window.DEBUG_SECTION_FINDING) {
      console.log('🐛 [DEBUG] Simple path - searching for direct heading');
    }
    
    for (const heading of headings) {
      const headingText = heading.textContent.trim().toLowerCase();
      const searchText = parts[0].toLowerCase();
      console.log(`  Comparing "${headingText}" with "${searchText}"`);
      if (headingText.includes(searchText) || searchText.includes(headingText)) {
        targetHeading = heading;
        console.log('✅ Found matching heading:', heading.textContent);
        
        if (window.DEBUG_SECTION_FINDING) {
          console.log('🐛 [DEBUG] Found simple heading match:', heading.textContent, 'tag:', heading.tagName);
        }
        
        break;
      }
    }
  } else {
    // NESTED HEADING (Requirements > Purpose OR Requirements > Requirements > Visual variants)
    console.log('🔎 Searching for nested heading:', parts);
    console.log('📑 Total headings found:', headings.length);
    
    if (window.DEBUG_SECTION_FINDING) {
      console.log('🐛 [DEBUG] Nested path detected - will search hierarchically');
    }
    
    // Start at the root
    let currentIndex = -1;
    let currentLevel = 0;
    
    // Walk through each part of the path
    for (let partIndex = 0; partIndex < parts.length; partIndex++) {
      const targetPart = parts[partIndex].toLowerCase();
      console.log(`\n🔍 Looking for part ${partIndex + 1}/${parts.length}: "${targetPart}"`);
      console.log(`   Searching from index ${currentIndex + 1}, must be level > ${currentLevel}`);
      
      if (window.DEBUG_SECTION_FINDING) {
        console.log(`🐛 [DEBUG] Part ${partIndex + 1}:`, targetPart);
        console.log('🐛 [DEBUG] Starting from heading index:', currentIndex + 1, 'must be deeper than level:', currentLevel);
      }
      
      let found = false;
      
      // Search for this part after the current position
      for (let i = currentIndex + 1; i < headings.length; i++) {
        const heading = headings[i];
        const headingText = heading.textContent.trim().toLowerCase();
        const headingLevel = parseInt(heading.tagName.substring(1));
        
        // Must be deeper than current level (child of current heading)
        if (headingLevel <= currentLevel) {
          console.log(`   ⏭️ Skipping "${heading.textContent}" (level ${headingLevel} not > ${currentLevel})`);
          continue;
        }
        
        console.log(`   Comparing "${heading.textContent.trim()}" (level ${headingLevel}) with "${targetPart}"`);
        
        if (headingText === targetPart) {
          console.log(`   ✅ Found part ${partIndex + 1}: "${heading.textContent}" at index ${i}, level ${headingLevel}`);
          
          if (window.DEBUG_SECTION_FINDING) {
            console.log(`🐛 [DEBUG] ✅ MATCH found as HEADING at index ${i}:`, heading.textContent);
            console.log('🐛 [DEBUG] Heading level:', headingLevel, 'tag:', heading.tagName);
          }
          
          currentIndex = i;
          currentLevel = headingLevel;
          found = true;
          break;
        }
      }
      
      if (!found) {
        console.error(`❌ Could not find part "${targetPart}" in path`);
        
        if (window.DEBUG_SECTION_FINDING) {
          console.log('🐛 [DEBUG] Part not found as heading, checking for bold labels...');
        }
        
        // If we're on the last part and haven't found it as a heading, check for bold labels
        if (partIndex === parts.length - 1 && currentIndex >= 0) {
          console.log('🔍 Checking bold labels within parent section...');
          
          // Get parent section content (between parent heading and next same-level heading)
          const parentHeading = headings[currentIndex];
          const parentLevel = currentLevel;
          let nextSiblingIndex = -1;
          for (let i = currentIndex + 1; i < headings.length; i++) {
            const level = parseInt(headings[i].tagName.charAt(1));
            if (level <= parentLevel) {
              nextSiblingIndex = i;
              break;
            }
          }
          
          // Get parent section's DOM elements
          let sectionContent = [];
          let currentElement = parentHeading.nextElementSibling;
          const endElement = nextSiblingIndex >= 0 ? headings[nextSiblingIndex] : null;
          
          while (currentElement && currentElement !== endElement) {
            sectionContent.push(currentElement);
            currentElement = currentElement.nextElementSibling;
          }
          
          // Search for bold labels in section content
          for (const element of sectionContent) {
            const strongTags = element.querySelectorAll('strong, b');
            for (const strong of strongTags) {
              const labelText = strong.textContent.trim();
              // Check if it ends with a colon and matches our search
              if (labelText.endsWith(':')) {
                const labelWithoutColon = labelText.slice(0, -1).toLowerCase();
                console.log(`  Bold label check: "${labelWithoutColon}" vs "${targetPart}"`);
                if (labelWithoutColon === targetPart) {
                  console.log('✅ Found bold label:', labelText);
                  
                  if (window.DEBUG_SECTION_FINDING) {
                    console.log('🐛 [DEBUG] ✅ MATCH found as BOLD LABEL:', labelText);
                    console.log('🐛 [DEBUG] Bold label is within parent:', parts[partIndex - 1]);
                  }
                  
                  // Create a pseudo-heading element to return
                  targetHeading = {
                    textContent: labelText,
                    tagName: 'STRONG',
                    isBoldLabel: true,
                    element: strong
                  };
                  found = true;
                  break;
                }
              }
            }
            if (found) break;
          }
        }
        
        if (!found) {
          if (window.DEBUG_SECTION_FINDING) {
            console.log('🐛 [DEBUG] ❌ Part not found as heading or bold label');
          }
          return null;
        }
      }
    }
    
    // Found the final heading in the path (unless it was a bold label)
    if (!targetHeading) {
      targetHeading = headings[currentIndex];
      console.log('🎯 Target heading found:', targetHeading.textContent);
      
      if (window.DEBUG_SECTION_FINDING) {
        console.log('🐛 [DEBUG] Final target from nested search:', targetHeading.textContent);
      }
    }
  }
  
  if (!targetHeading) {
    console.error('❌ Section not found:', sectionPath);
    
    if (window.DEBUG_SECTION_FINDING) {
      console.log('🐛 [DEBUG] ❌ FINAL RESULT: NOT FOUND');
    }
    
    return { found: false, error: `Section "${sectionPath}" not found` };
  }
  
  console.log('🎯 Target heading found:', targetHeading.textContent);
  
  // Calculate position in the ProseMirror document
  const doc = editor.state.doc;
  let position = null;
  let endPosition = null;
  
  // Check if this is a bold label or a regular heading
  if (targetHeading.isBoldLabel) {
    // For bold labels, we need to find the paragraph containing the label
    const labelText = targetHeading.textContent.trim();
    console.log('🔍 Searching for bold label in editor:', labelText);
    
    // Search through the document to find the paragraph with this strong text
    doc.descendants((node, pos) => {
      if (node.type.name === 'paragraph') {
        // Check if this paragraph contains the label text
        const paragraphText = node.textContent.trim();
        if (paragraphText.startsWith(labelText) || paragraphText.includes(labelText)) {
          console.log('📍 Found paragraph with bold label at pos:', pos);
          // Position should be right after the label text
          position = pos + labelText.length + 1; // +1 for the paragraph node itself
          
          // Find the end of this label's content (before next bold label or heading)
          let nextLabelPos = null;
          let searchPos = pos + node.nodeSize;
          
          doc.descendants((searchNode, searchNodePos) => {
            if (searchNodePos <= searchPos) return true;
            
            // Stop at next heading
            if (searchNode.type.name === 'heading') {
              nextLabelPos = searchNodePos;
              return false;
            }
            
            // Stop at next paragraph that starts with bold text ending in colon
            if (searchNode.type.name === 'paragraph') {
              const text = searchNode.textContent.trim();
              // Check for patterns like "Label:" at the start
              if (/^[^:]+:/.test(text)) {
                nextLabelPos = searchNodePos;
                return false;
              }
            }
            
            return true;
          });
          
          endPosition = nextLabelPos || doc.content.size;
          console.log('📍 Bold label section ends at:', endPosition);
          return false; // Stop searching
        }
      }
      return true;
    });
    
    if (position === null) {
      console.error('❌ Could not locate bold label in editor');
      
      if (window.DEBUG_SECTION_FINDING) {
        console.log('🐛 [DEBUG] ❌ FINAL RESULT: Bold label found in HTML but not in editor doc');
      }
      
      return { found: false, error: 'Could not locate bold label in editor' };
    }
    
    // For bold labels, we don't have a heading level, so use 0
    console.log('✅ Bold label section found successfully');
    
    if (window.DEBUG_SECTION_FINDING) {
      console.log('🐛 [DEBUG] ✅ FINAL RESULT: Bold label found');
      console.log('🐛 [DEBUG] Position:', position, 'End:', endPosition, 'Level: 0 (bold label)');
    }
    
    return { found: true, position, endPosition, headingLevel: 0, isBoldLabel: true };
  } else {
    // Regular heading handling
    const headingText = targetHeading.textContent;
    const headingLevel = parseInt(targetHeading.tagName.substring(1)); // Extract level from H1, H2, etc.
    
    // Search through the document to find the heading
    doc.descendants((node, pos) => {
      if (node.type.name === 'heading' && node.textContent === headingText) {
        position = pos;
        endPosition = pos + node.nodeSize;
        console.log('📍 Found position in editor:', position, 'to', endPosition);
        return false; // Stop searching
      }
    });
    
    if (position === null) {
      console.error('❌ Could not locate position in editor');
      
      if (window.DEBUG_SECTION_FINDING) {
        console.log('🐛 [DEBUG] ❌ FINAL RESULT: Heading found in HTML but not in editor doc');
      }
      
      return { found: false, error: 'Could not locate position in editor' };
    }
    
    console.log('✅ Section found successfully, level:', headingLevel);
    
    if (window.DEBUG_SECTION_FINDING) {
      console.log('🐛 [DEBUG] ✅ FINAL RESULT: Heading found');
      console.log('🐛 [DEBUG] Position:', position, 'End:', endPosition, 'Level:', headingLevel);
    }
    
    return { found: true, position, endPosition, headingLevel };
  }
}

function findSectionContentEnd(editor, sectionStartPos, headingLevel) {
  console.log('🔍 Finding section content end from position:', sectionStartPos, 'heading level:', headingLevel);
  console.log('🎯 Target section level:', headingLevel);
  
  const doc = editor.state.doc;
  const targetLevel = headingLevel;
  
  // If headingLevel is 0, this is a bold label section
  if (headingLevel === 0) {
    console.log('🏷️ Finding end of bold label section...');
    const matchingPositions = [];
    
    // Collect all possible end positions
    doc.descendants((node, pos) => {
      // Skip everything before the section start
      if (pos <= sectionStartPos) {
        return;
      }
      
      // Stop at next heading
      if (node.type.name === 'heading') {
        matchingPositions.push(pos);
        console.log('📍 Found heading at pos:', pos);
      }
      
      // Stop at next paragraph that starts with bold text ending in colon (another bold label)
      if (node.type.name === 'paragraph') {
        const text = node.textContent.trim();
        if (/^[^:]+:/.test(text)) {
          matchingPositions.push(pos);
          console.log('📍 Found bold label at pos:', pos);
        }
      }
    });
    
    // Take the FIRST match
    if (matchingPositions.length > 0) {
      const firstMatch = matchingPositions[0];
      console.log('✅ Using FIRST match: pos', firstMatch);
      console.log('🔚 Next section starts at:', firstMatch);
      return firstMatch;
    }
    
    console.log('🔚 No next section found, section goes to end of document');
    return doc.content.size;
  }
  
  // Original heading-based logic - collect ALL matches, then take the first
  const matchingHeadings = [];
  
  doc.descendants((node, pos) => {
    // Skip nodes before or at the start position
    if (pos <= sectionStartPos) {
      return;
    }
    
    // Look for headings at the same or higher level
    if (node.type.name === 'heading') {
      const level = node.attrs.level;
      const text = node.textContent.trim();
      
      if (level <= targetLevel) {
        console.log('🔎 Found matching heading at pos', pos, 'level:', level, 'text:', text);
        matchingHeadings.push({ pos, level, text });
      }
    }
  });
  
  // Take the FIRST match (closest to start position)
  if (matchingHeadings.length > 0) {
    const firstMatch = matchingHeadings[0];
    console.log('✅ Using FIRST match: pos', firstMatch.pos, 'level', firstMatch.level, 'text:', firstMatch.text);
    console.log('📊 Total matches found:', matchingHeadings.length);
    return firstMatch.pos;
  }
  
  console.log('🔚 No next section found, section goes to end of document');
  return doc.content.size;
}

function highlightInsertedContent(start, end) {
  const editor = getEditor();
  if (!editor) {
    console.error('❌ Editor not available for highlighting');
    return;
  }
  
  console.log('📍 Scrolling to inserted content at position:', start);
  
  // Get the DOM coordinates for the start position
  const coords = editor.view.coordsAtPos(start);
  
  // Find the scrollable editor container
  const editorElement = document.querySelector('.tiptap.ProseMirror');
  const scrollContainer = editorElement?.closest('.editor-content') || editorElement?.parentElement;
  
  if (scrollContainer && coords) {
    // Calculate scroll position to put content near the top (with some padding)
    const containerRect = scrollContainer.getBoundingClientRect();
    const targetScrollTop = scrollContainer.scrollTop + (coords.top - containerRect.top) - 80; // 80px padding from top
    
    scrollContainer.scrollTo({
      top: Math.max(0, targetScrollTop),
      behavior: 'smooth'
    });
    
    console.log('✅ Scrolled to inserted content (near top)');
  } else {
    // Fallback to TipTap's scroll
    editor.commands.setTextSelection(start);
    editor.commands.scrollIntoView();
    console.log('✅ Scrolled to inserted content (fallback)');
  }
}

async function insertContentIntoEditor(action, section, markdownContent) {
  console.log('📝 insertContentIntoEditor called');
  console.log('  Action:', action);
  console.log('  Section:', section);
  console.log('  Content length:', markdownContent.length);
  
  const editor = getEditor();
  if (!editor) {
    console.error('❌ Editor not initialized');
    return { success: false, error: 'Editor not initialized' };
  }
  
  console.log('✅ Editor instance obtained');
  
  try {
    // Convert markdown to HTML
    console.log('🔄 Converting markdown to HTML...');
    const html = marked.parse(markdownContent);
    console.log('✅ Markdown converted, HTML length:', html.length);
    
    // Find the section
    console.log('🔍 Finding section in editor...');
    const sectionResult = findSectionInEditor(section);
    
    if (!sectionResult.found) {
      console.error('❌ Section not found:', sectionResult.error);
      return { success: false, error: sectionResult.error };
    }
    
    console.log('✅ Section found');
    const { position, endPosition, headingLevel } = sectionResult;
    console.log('📍 Section heading position:', position, 'to', endPosition, 'level:', headingLevel);
    
    let insertStart = endPosition;
    let insertEnd = endPosition;
    let originalContent = null;
    
    console.log(`🎬 Action: ${action} - ${action === 'replace' ? 'Replacing' : 'Appending to'} section`);
    
    if (action === 'append') {
      console.log('➕ Appending content to end of section...');
      // Find where this section's content ends
      const sectionContentEnd = findSectionContentEnd(editor, endPosition, headingLevel);
      console.log('📍 Section content ends at:', sectionContentEnd);
      
      // Capture document size BEFORE insertion
      const docSizeBefore = editor.state.doc.content.size;
      
      // Insert at the end of the section content
      editor.commands.focus();
      editor.commands.insertContentAt(sectionContentEnd, html);
      
      // Calculate actual inserted size from document change
      const docSizeAfter = editor.state.doc.content.size;
      const actualInsertedSize = docSizeAfter - docSizeBefore;
      
      insertStart = sectionContentEnd;
      insertEnd = sectionContentEnd + actualInsertedSize;
      console.log('✅ Content appended at position:', insertStart, 'to', insertEnd);
      console.log('📏 Actual inserted size:', actualInsertedSize);
    } else if (action === 'replace') {
      console.log('🔄 Replacing section content...');
      console.log('🎯 Target section level:', headingLevel);
      
      // Find where this section's content ends
      const sectionContentEnd = findSectionContentEnd(editor, endPosition, headingLevel);
      console.log('📏 Replacing content from', endPosition, 'to', sectionContentEnd);
      
      // Capture original content before deleting
      const contentLength = sectionContentEnd - endPosition;
      if (contentLength > 0) {
        // Capture the document slice as JSON to preserve all formatting
        const slice = editor.state.doc.slice(endPosition, sectionContentEnd);
        // Store just the content array, not the full Slice object
        originalContent = JSON.stringify(slice.content.toJSON());
        console.log('💾 Captured original content array, length:', originalContent.length);
        
        console.log('🗑️ Deleting', contentLength, 'characters of existing content');
        editor.commands.focus();
        editor.commands.deleteRange({ from: endPosition, to: sectionContentEnd });
      }
      
      // Capture document size BEFORE insertion
      const docSizeBefore = editor.state.doc.content.size;
      
      // Insert new content at the section start (after heading)
      editor.commands.focus();
      editor.commands.insertContentAt(endPosition, html);
      
      // Calculate actual inserted size from document change
      const docSizeAfter = editor.state.doc.content.size;
      const actualInsertedSize = docSizeAfter - docSizeBefore;
      
      insertStart = endPosition;
      insertEnd = endPosition + actualInsertedSize;
      console.log('✅ Content replaced at position:', insertStart, 'to', insertEnd);
      console.log('📏 Actual inserted size:', actualInsertedSize);
    }
    
    // Debug: Log the actual content at the calculated positions
    try {
      const insertedSlice = editor.state.doc.slice(insertStart, insertEnd);
      console.log('🔍 DEBUG - Calculated range:', insertStart, 'to', insertEnd);
      console.log('🔍 DEBUG - Content at range:', insertedSlice.content.textBetween(0, insertedSlice.content.size, ' '));
    } catch (e) {
      console.log('🔍 DEBUG - Could not log range content:', e.message);
    }
    
    console.log('✅ Insert operation completed successfully');
    console.log('📍 Inserted content range:', insertStart, 'to', insertEnd);
    return { 
      success: true, 
      start: insertStart, 
      end: insertEnd, 
      originalContent, 
      action 
    };
  } catch (error) {
    console.error('💥 Error in insertContentIntoEditor:', error);
    return { success: false, error: error.message };
  }
}

function processAIMessageWithInserts(content) {
  // Regex to match INSERT blocks
  const insertRegex = /\[INSERT: action="(.*?)" section="(.*?)" label="(.*?)"\]([\s\S]*?)\[\/INSERT\]/g;
  
  let processedContent = content;
  let match;
  const insertBlocks = [];
  
  // Find all INSERT blocks
  while ((match = insertRegex.exec(content)) !== null) {
    const [fullMatch, action, section, label, blockContent] = match;
    
    insertBlocks.push({
      fullMatch,
      action,
      section,
      label,
      content: blockContent.trim()
    });
  }
  
  // Replace each INSERT block with a placeholder comment
  insertBlocks.forEach((insertData, index) => {
    const placeholder = `<!--INSERT_PLACEHOLDER_${index}-->`;
    processedContent = processedContent.replace(insertData.fullMatch, placeholder);
  });
  
  return { processedContent, insertBlocks };
}

function processAIMessageWithWorkItems(content) {
  if (!content || content.length === 0) {
    console.log('🔍 Work Items Detection - Empty content');
    return { hasIssues: false, issueContent: null };
  }

  // Detect work item content - look for "Feature:" or "Title:" followed by component name pattern
  // and "Story 1:", "Story 2:", "Story 3:" patterns
  // Case-insensitive to handle FEATURE:/Feature:/feature: and STORY/Story/story
  console.log('🔍 Work Items Detection - Checking content...');
  console.log('📝 Content length:', content.length);
  console.log('📝 Content preview (first 500 chars):', content.substring(0, 500));
  
  // First, check if content is wrapped in markdown code blocks
  // Pattern: ```markdown ... ``` or ``` ... ```
  // Handle both with and without newlines after opening ```
  let contentToSearch = content;
  const codeBlockMatch = content.match(/```(?:markdown)?\s*\n?([\s\S]+?)\n?```/);
  if (codeBlockMatch) {
    console.log('📦 Found content inside markdown code block, extracting...');
    contentToSearch = codeBlockMatch[1].trim();
    console.log('📝 Extracted content length:', contentToSearch.length);
    console.log('📝 Extracted content preview:', contentToSearch.substring(0, 500));
  }
  
  // More flexible regex - look for Feature/Title anywhere (not just start of line)
  // and Story patterns
  // Try multiple patterns to catch different formats
  
  // Pattern 1: ## Feature: Component: Card or Feature: Component: Card (with markdown headers)
  // Updated to handle cases where there might be text before the Feature header
  // Allow 0-6 hash symbols to match both "Feature:" and "# Feature:" formats
  const featurePattern1 = /(?:^|\n)#{0,6}\s*(?:Feature|FEATURE):\s*(?:Component:)?\s*.+?/im;
  
  // Pattern 2: Just "Feature:" or "FEATURE:" anywhere (case-insensitive, more flexible)
  // This is a fallback that should catch Feature: even if it's not at line start
  const featurePattern2 = /(?:Feature|FEATURE):\s*(?:Component:)?\s*.+?/i;
  
  // Story patterns - look for Story 1:, Story 2:, Story 3:, STORY 1:, etc.
  // Match the server's pattern: /^#{0,6}\s*(?:Story|STORY)\s+\d+:/gim
  // Also handle markdown formatting and ensure we match at line start
  // Use multiline flag to make ^ match start of each line
  const storyPattern = /^#{0,6}\s*(?:Story|STORY)\s+\d+:/gim;
  
  // Also try a more flexible pattern that doesn't require line start (fallback)
  const storyPatternFlexible = /(?:^|\n)#{0,6}\s*(?:Story|STORY)\s+\d+:/gi;
  
  const hasFeature1 = featurePattern1.test(contentToSearch);
  const hasFeature2 = featurePattern2.test(contentToSearch);
  
  // Debug: Show what feature patterns matched
  const featureMatch1 = contentToSearch.match(featurePattern1);
  const featureMatch2 = contentToSearch.match(featurePattern2);
  
  // Try both story patterns - prefer the stricter pattern, fallback to flexible
  const storyMatches = [...contentToSearch.matchAll(storyPattern)];
  const storyMatchesFlexible = storyMatches.length >= 2 
    ? storyMatches 
    : [...contentToSearch.matchAll(storyPatternFlexible)];
  
  const hasStories = storyMatchesFlexible.length >= 2; // Need at least 2 stories (Design, Develop, Document)
  
  console.log('🔍 Detection results:', { 
    hasFeature1, 
    hasFeature2,
    featureMatch1: featureMatch1 ? featureMatch1[0].substring(0, 100) : null,
    featureMatch2: featureMatch2 ? featureMatch2[0].substring(0, 100) : null,
    storyCount: storyMatchesFlexible.length,
    hasStories,
    storyMatches: storyMatchesFlexible.map(m => m[0]),
    contentSample: contentToSearch.substring(0, 1000),
    contentLength: contentToSearch.length
  });
  
  // If we have feature AND at least 2 stories, this looks like work item content
  if ((hasFeature1 || hasFeature2) && hasStories) {
    console.log('✅ Work items detected! Extracting content...');
    
    // Find the start - look for Feature: or FEATURE: (with optional markdown headers)
    // Try multiple patterns to find the start
    let startMatch = contentToSearch.match(/(?:^|\n)#{0,6}\s*(?:Feature|FEATURE):/im);
    if (!startMatch) {
      // Fallback: look for Feature: without markdown header or line start requirement
      startMatch = contentToSearch.match(/(?:Feature|FEATURE):/i);
    }
    if (!startMatch) {
      console.log('❌ No start match found for Feature');
      return { hasIssues: false, issueContent: null };
    }
    
    const startIndex = startMatch.index + (startMatch[0].startsWith('\n') ? 1 : 0);
    console.log('📍 Start index:', startIndex, 'Match:', startMatch[0]);
    
    // Find the end - look for the last Story section
    if (storyMatchesFlexible.length === 0) {
      console.log('❌ No story matches found');
      return { hasIssues: false, issueContent: null };
    }
    
    const lastStoryIndex = storyMatchesFlexible[storyMatchesFlexible.length - 1].index;
    console.log('📍 Last story index:', lastStoryIndex);
    
    // Find the end of the last story (look for end of content or concluding text)
    const remainingContent = contentToSearch.substring(lastStoryIndex);
    const conclusionMatch = remainingContent.match(/\n\n(?:These [\s\S]{0,100}? issues|Epic Completion|---|\n\n[A-Z][a-z]+ [A-Z])/i);
    const endIndex = lastStoryIndex + (conclusionMatch ? conclusionMatch.index : remainingContent.length);
    
    const issueContent = contentToSearch.substring(startIndex, endIndex).trim();
    
    console.log('✅ Extracted issue content, length:', issueContent.length);
    console.log('📋 Issue content start:', issueContent.substring(0, 300));
    console.log('📋 Issue content end:', issueContent.substring(Math.max(0, issueContent.length - 300)));
    
    return { hasIssues: true, issueContent };
  }
  
  console.log('❌ Work items NOT detected - missing requirements');
  return { hasIssues: false, issueContent: null };
}

async function createMessageElement(msg, index) {
  const msgDiv = document.createElement('div');
  msgDiv.className = `ai-message ${msg.role}`;
  msgDiv.dataset.index = index;
  
  // Create message content wrapper
  const contentWrapper = document.createElement('div');
  contentWrapper.style.cssText = 'display: flex; flex-direction: column; width: 100%;';
  
  // Message text
  const textDiv = document.createElement('div');
  
  if (msg.role === 'assistant') {
    // Process INSERT blocks first - get placeholders and block data
    const { processedContent, insertBlocks } = processAIMessageWithInserts(msg.content || '');
    
    // Check for work item content
    const { hasIssues, issueContent } = processAIMessageWithWorkItems(msg.content || '');
    
    // Remove blank lines by replacing all double newlines with single newlines
    const cleanedContent = processedContent.replace(/\n\n/g, '\n');
    
    let html = marked.parse(cleanedContent);
    // Remove any <br> tags that might be causing extra spacing
    html = html.replace(/<br\s*\/?>/gi, '');
    
    textDiv.innerHTML = html;
    textDiv.classList.add('markdown-content');
    
    // Replace placeholder comments with actual INSERT block DOM elements
    insertBlocks.forEach((insertData, index) => {
      const placeholder = `INSERT_PLACEHOLDER_${index}`;
      // Find the comment node
      const walker = document.createTreeWalker(
        textDiv,
        NodeFilter.SHOW_COMMENT,
        null,
        false
      );
      
      while (walker.nextNode()) {
        if (walker.currentNode.nodeValue === placeholder) {
          // Replace the comment with the INSERT block element
          const insertElement = renderInsertBlock(insertData, msg.targetCrd);
          walker.currentNode.parentNode.replaceChild(insertElement, walker.currentNode);
          break;
        }
      }
    });
    
    // Add work item creation button if detected
    if (hasIssues && issueContent) {
      const issueButton = await renderWorkItemButton(issueContent, msg.targetCrd);
      textDiv.appendChild(issueButton);
    }
  } else {
    // Plain text for user messages
    textDiv.textContent = msg.content;
    textDiv.style.cssText = 'white-space: pre-wrap; word-wrap: break-word;';
  }
  
  contentWrapper.appendChild(textDiv);
  
  // Add copy button for assistant messages
  if (msg.role === 'assistant' && msg.content) {
    const copyBtn = document.createElement('button');
    copyBtn.innerHTML = '<i class="ph ph-copy"></i>';
    copyBtn.style.cssText = `
      align-self: flex-start;
      margin-top: 8px;
      padding: 4px 8px;
      background: #f5f5f5;
      border: 1px solid #e5e5e5;
      border-radius: 4px;
      color: #141414;
      cursor: pointer;
      font-size: 14px;
      transition: all 0.15s;
    `;
    copyBtn.addEventListener('click', () => copyToClipboard(msg.content, copyBtn));
    copyBtn.addEventListener('mouseenter', () => {
      copyBtn.style.background = '#fef2f7';
      copyBtn.style.borderColor = '#ec4899';
      copyBtn.style.color = '#ec4899';
    });
    copyBtn.addEventListener('mouseleave', () => {
      copyBtn.style.background = '#f5f5f5';
      copyBtn.style.borderColor = '#e5e5e5';
      copyBtn.style.color = '#141414';
    });
    contentWrapper.appendChild(copyBtn);
  }
  
  msgDiv.appendChild(contentWrapper);
  return msgDiv;
}

async function handleSendMessage() {
  const aiInput = document.getElementById('aiInput');
  const aiSendBtn = document.getElementById('aiSend');
  const message = aiInput.value.trim();
  
  console.log('📤 Sending message:', message);
  
  if (!message || isStreaming) {
    console.log('⏸️ Message empty or already streaming, ignoring');
    return;
  }
  
  // Disable send button immediately to prevent duplicate sends
  aiSendBtn.disabled = true;
  aiSendBtn.style.opacity = '0.5';
  aiSendBtn.style.cursor = 'not-allowed';
  
  // Clear input immediately for instant feedback
  aiInput.value = '';
  console.log('✅ Input cleared and button disabled');
  
  // Add user message
  const userMsg = {
    role: 'user',
    content: message,
    timestamp: new Date().toISOString(),
    targetCrd: {
      folder: currentCrdFolder || '',
      filename: currentCrdFilename || ''
    }
  };
  chatHistory.push(userMsg);
  
  // Add empty assistant message slot
  const aiMsg = {
    role: 'assistant',
    content: '',
    timestamp: new Date().toISOString(),
    targetCrd: {
      folder: currentCrdFolder || '',
      filename: currentCrdFilename || ''
    }
  };
  chatHistory.push(aiMsg);
  
  console.log('💬 Rendering user message...');
  renderChatHistory();
  
  // Show loading indicator
  isStreaming = true;
  const aiChatContainer = document.getElementById('aiChat');
  const loadingDiv = document.createElement('div');
  loadingDiv.className = 'ai-message assistant streaming';
  loadingDiv.innerHTML = '<div class="typing-indicator"><span></span><span></span><span></span></div>';
  aiChatContainer.appendChild(loadingDiv);
  scrollToBottom();
  console.log('⏳ Loading indicator shown');
  
  try {
    console.log('📡 Sending request to /api/ai/chat...');
    
    // Get current CRD context
    const currentCRD = await getCurrentCRD();
    
    // Debug: Log current CRD context
    console.log('🔍 Current CRD object:', currentCRD);
    console.log('  - Folder:', currentCRD?.folder);
    console.log('  - Filename:', currentCRD?.filename);
    
    // Build request body
    // Limit chat history to last 50 messages to prevent payload size issues
    // (Each message can be large, so we keep recent context but not entire history)
    const recentHistory = chatHistory.slice(0, -1).filter(m => m.content).slice(-50);
    
    const requestBody = {
      message: message,
      chatHistory: recentHistory
    };
    
    console.log(`📊 Sending ${recentHistory.length} messages in chat history (limited to last 50)`);
    
    // Only include CRD info if a CRD is open
    if (currentCRD && currentCRD.folder && currentCRD.filename) {
      requestBody.crdFolder = currentCRD.folder;
      requestBody.crdFilename = currentCRD.filename;
      console.log('📄 Including CRD context:', currentCRD.folder, '/', currentCRD.filename);
    } else {
      console.log('💬 No CRD open, sending general chat request');
    }
    
    // Debug: Log request body
    console.log('📦 Request body:', requestBody);
    
    // Send to API
    const response = await apiPost('/api/ai/chat', requestBody);
    
    console.log('📨 Response received, status:', response.status);
    
    if (!response.ok) {
      // Try to get error message from response
      let errorMessage = `HTTP error ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch (e) {
        // Could not parse error response
      }
      throw new Error(errorMessage);
    }
    
    console.log('✅ SSE connection established, waiting for response...');
    
    // Process SSE stream - update only the last message incrementally
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let sseBuffer = '';
    const aiChatContainer = document.getElementById('aiChat');
    
    // Create the assistant message element that will be updated (hidden until first delta)
    const lastMsg = chatHistory[chatHistory.length - 1];
    const streamingMsgEl = await createMessageElement(lastMsg, chatHistory.length - 1);
    streamingMsgEl.style.display = 'none';
    aiChatContainer.appendChild(streamingMsgEl);
    
    // Get the text div that will be updated
    const textDiv = streamingMsgEl.querySelector('.markdown-content') || streamingMsgEl.querySelector('div');
    
    // Track whether we have shown real content yet
    let firstDeltaReceived = false;
    
    console.log('📥 Starting to receive streaming response...');
    
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      
      sseBuffer += decoder.decode(value, { stream: true });
      const lines = sseBuffer.split(/\r?\n/);
      sseBuffer = lines.pop() || '';
      
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            
            if (data.type === 'status') {
              // Update the loading indicator text with the current server status
              const typingIndicator = loadingDiv.querySelector('.typing-indicator');
              if (typingIndicator) {
                loadingDiv.innerHTML = `<div class="typing-indicator"><span></span><span></span><span></span></div><span class="ai-status-text">${data.message}</span>`;
              }
            } else if (data.type === 'delta') {
              // First real content - swap loading indicator for message element
              if (!firstDeltaReceived) {
                firstDeltaReceived = true;
                loadingDiv.remove();
                streamingMsgEl.style.display = '';
                console.log('✅ Loading indicator removed, displaying response...');
              }
              // Append to last message content
              chatHistory[chatHistory.length - 1].content += data.text;
              
              // Process INSERT blocks and render markdown
              const { processedContent, insertBlocks } = processAIMessageWithInserts(chatHistory[chatHistory.length - 1].content);
              let html = marked.parse(processedContent);
              
              textDiv.innerHTML = html;
              textDiv.classList.add('markdown-content');
              
              // Replace placeholder comments with actual INSERT block DOM elements
              insertBlocks.forEach((insertData, index) => {
                const placeholder = `INSERT_PLACEHOLDER_${index}`;
                const walker = document.createTreeWalker(
                  textDiv,
                  NodeFilter.SHOW_COMMENT,
                  null,
                  false
                );
                
                while (walker.nextNode()) {
                  if (walker.currentNode.nodeValue === placeholder) {
                    const insertElement = renderInsertBlock(insertData);
                    walker.currentNode.parentNode.replaceChild(insertElement, walker.currentNode);
                    break;
                  }
                }
              });
              
              // Auto-scroll to bottom
              scrollToBottom();
            } else if (data.type === 'complete') {
              console.log('✅ AI response complete');
              isStreaming = false; // Mark streaming as complete FIRST
              
              // Verify we have the complete message
              const lastMessage = chatHistory[chatHistory.length - 1];
              if (!lastMessage || !lastMessage.content) {
                console.error('❌ No message content found after completion');
                return;
              }
              
              console.log('📋 Full message content length:', lastMessage.content.length);
              console.log('📋 Full message content preview:', lastMessage.content.substring(0, 500));
              
              // Final render with complete content
              const { processedContent, insertBlocks } = processAIMessageWithInserts(lastMessage.content);
              let html = marked.parse(processedContent);
              textDiv.innerHTML = html;
              textDiv.classList.add('markdown-content');
              
              // Replace placeholder comments with actual INSERT block DOM elements
              insertBlocks.forEach((insertData, index) => {
                const placeholder = `INSERT_PLACEHOLDER_${index}`;
                const walker = document.createTreeWalker(
                  textDiv,
                  NodeFilter.SHOW_COMMENT,
                  null,
                  false
                );
                
                while (walker.nextNode()) {
                  if (walker.currentNode.nodeValue === placeholder) {
                    const insertElement = renderInsertBlock(insertData);
                    walker.currentNode.parentNode.replaceChild(insertElement, walker.currentNode);
                    break;
                  }
                }
              });
              
              // Check for work items and add button if detected - AFTER full content is rendered
              // IMPORTANT: Use the complete content from lastMessage, ensure streaming is complete
              if (isStreaming) {
                console.warn('⚠️ Warning: Still streaming when checking for work items');
              }

              const fullContent = lastMessage.content;

              console.log('🔍 Final check for work items with full content...');
              console.log('📏 Content length:', fullContent.length);
              console.log('📄 Content preview (first 1000 chars):', fullContent.substring(0, 1000));

              const { hasIssues, issueContent } = processAIMessageWithWorkItems(fullContent);

              console.log('🔍 Detection result:', { hasIssues, issueContentLength: issueContent?.length || 0 });

              if (hasIssues && issueContent) {
                console.log('✅ Work items detected! Adding button...');
                console.log('📋 Work item content preview:', issueContent.substring(0, 500));

                // Ensure we have the correct textDiv reference
                if (!textDiv) {
                  console.error('❌ textDiv is null, cannot add button');
                } else {
                  // Remove any existing work item button (in case it was added during streaming)
                  const existingButton = textDiv.querySelector('.github-issue-block');
                  if (existingButton) {
                    console.log('🗑️ Removing existing button');
                    existingButton.remove();
                  }

                  // Add work item button
                  try {
                    const issueButton = await renderWorkItemButton(issueContent, lastMessage.targetCrd);
                    textDiv.appendChild(issueButton);
                    console.log('✅ Button added successfully to textDiv');
                    scrollToBottom();
                  } catch (error) {
                    console.error('❌ Error adding button:', error);
                  }
                }
              } else {
                console.log('❌ No work items detected in final check');
                console.log('   - hasIssues:', hasIssues);
                console.log('   - issueContent:', issueContent ? 'exists' : 'null');
              }
              
              // Re-enable send button
              aiSendBtn.disabled = false;
              aiSendBtn.style.opacity = '1';
              aiSendBtn.style.cursor = 'pointer';
              console.log('✅ Send button re-enabled');
              
              saveChatHistory();
            } else if (data.type === 'error') {
              console.error('❌ AI error:', data.message);
              // Clean up loading state if no delta was received yet
              if (!firstDeltaReceived) {
                loadingDiv.remove();
                streamingMsgEl.style.display = '';
              }
              chatHistory[chatHistory.length - 1].content = `Error: ${data.message}`;
              renderChatHistory();
              isStreaming = false;
              
              // Re-enable send button
              aiSendBtn.disabled = false;
              aiSendBtn.style.opacity = '1';
              aiSendBtn.style.cursor = 'pointer';
            }
          } catch (e) {
            console.error('Failed to parse SSE data:', e);
          }
        }
      }
    }
    
  } catch (error) {
    console.error('❌ Error sending message:', error);
    
    // Remove loading indicator
    const streamingMsg = document.querySelector('.ai-message.streaming');
    if (streamingMsg) streamingMsg.remove();
    
    // Update last message with error
    chatHistory[chatHistory.length - 1].content = 'Sorry, I encountered an error. Please try again.';
    renderChatHistory();
    
    isStreaming = false;
    
    // Re-enable send button
    aiSendBtn.disabled = false;
    aiSendBtn.style.opacity = '1';
    aiSendBtn.style.cursor = 'pointer';
    console.log('✅ Send button re-enabled after error');
  }
}

async function handleClearChat() {
  console.log('🧹 handleClearChat() function called');
  
  const confirmed = confirm('Clear entire conversation?');
  console.log('❓ User confirmation:', confirmed);
  
  if (!confirmed) {
    console.log('❌ User cancelled');
    return;
  }
  
  console.log('✅ User confirmed, clearing...');
  
  try {
    console.log('📡 Sending POST to /api/ai/clear...');
    
    // Call backend to clear server-side conversation
    const response = await apiPost('/api/ai/clear', {});
    
    console.log('📨 Response status:', response.status);
    console.log('📨 Response ok:', response.ok);
    
    if (response.ok) {
      const data = await response.json();
      console.log('📨 Response data:', data);
      
      // Clear frontend
      console.log('🗑️ Clearing chatHistory (current length:', chatHistory.length, ')');
      chatHistory = [];
      
      console.log('💾 Saving cleared history...');
      saveChatHistory();
      
      console.log('🎨 Rendering welcome message...');
      renderWelcomeMessage();
      
      console.log('✅ Chat history cleared successfully');
    } else {
      const errorText = await response.text();
      console.error('❌ Failed to clear chat on server, response:', errorText);
    }
  } catch (error) {
    console.error('❌ Failed to clear chat, error:', error);
  }
}

function copyToClipboard(text, button) {
  navigator.clipboard.writeText(text).then(() => {
    const originalHTML = button.innerHTML;
    button.innerHTML = '<i class="ph ph-check"></i> Copied!';
    button.style.background = '#d1fae5';
    button.style.borderColor = '#10b981';
    button.style.color = '#065f46';
    
    setTimeout(() => {
      button.innerHTML = originalHTML;
      button.style.background = '#f5f5f5';
      button.style.borderColor = '#e5e5e5';
      button.style.color = '#141414';
    }, 2000);
  }).catch(err => {
    console.error('Failed to copy:', err);
  });
}

function scrollToBottom() {
  const aiChatContainer = document.getElementById('aiChat');
  aiChatContainer.scrollTop = aiChatContainer.scrollHeight;
}

// Resizable layout functionality
export function initResizableLayout() {
  const handle = document.getElementById('aiResizeHandle');
  const aiSidebar = document.getElementById('aiSidebar');
  const canvas = document.getElementById('editorContainer');
  const explorer = document.getElementById('fileBrowser');
  
  if (!handle || !aiSidebar || !canvas) {
    console.error('❌ Missing layout elements for resize');
    return;
  }
  
  // Calculate smart initial width
  function calculateInitialWidth() {
    const windowWidth = window.innerWidth;
    const explorerWidth = explorer?.offsetWidth || 0;
    const availableSpace = windowWidth - explorerWidth - 600; // 600px min for canvas
    
    // Try saved width first
    const savedWidth = localStorage.getItem('aiSidebarWidth');
    if (savedWidth) {
      const saved = parseInt(savedWidth);
      if (saved >= 320 && saved <= availableSpace) {
        return saved;
      }
    }
    
    // Default: 600px or whatever fits
    return Math.min(600, Math.max(320, availableSpace));
  }
  
  // Set initial width
  const initialWidth = calculateInitialWidth();
  aiSidebar.style.width = `${initialWidth}px`;
  console.log('📏 AI sidebar initial width:', initialWidth);
  
  // Drag to resize
  let isResizing = false;
  let startX = 0;
  let startWidth = 0;
  
  handle.addEventListener('mousedown', (e) => {
    e.preventDefault();
    isResizing = true;
    startX = e.clientX;
    startWidth = aiSidebar.offsetWidth;
    
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    
    console.log('🖱️  Start resize:', { startX, startWidth });
  });
  
  document.addEventListener('mousemove', (e) => {
    if (!isResizing) return;
    
    // Calculate delta (positive = drag right = wider, negative = drag left = narrower)
    const delta = e.clientX - startX;
    const newWidth = startWidth + delta; // CHANGED: + instead of -
    
    // Calculate max width based on canvas minimum
    const containerWidth = aiSidebar.parentElement.offsetWidth;
    const explorerWidth = explorer?.offsetWidth || 0;
    const explorerDisplay = explorer ? window.getComputedStyle(explorer).display : 'none';
    const actualExplorerWidth = explorerDisplay === 'none' ? 0 : explorerWidth;
    const maxWidth = containerWidth - actualExplorerWidth - 600 - 1;
    
    // Apply constraints
    const constrainedWidth = Math.max(320, Math.min(newWidth, maxWidth, 1000));
    
    aiSidebar.style.width = `${constrainedWidth}px`;
  });
  
  document.addEventListener('mouseup', () => {
    if (isResizing) {
      isResizing = false;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      
      const finalWidth = aiSidebar.offsetWidth;
      localStorage.setItem('aiSidebarWidth', finalWidth);
      console.log('💾 Saved width:', finalWidth);
    }
  });
  
  // Smart window resize
  let resizeTimeout;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      const windowWidth = window.innerWidth;
      const explorerWidth = explorer?.offsetWidth || 0;
      const minRequired = explorerWidth + 600 + 320 + 1; // canvas + AI + handle
      
      if (windowWidth < minRequired) {
        // Shrink AI to fit
        const availableForAI = windowWidth - explorerWidth - 600 - 1;
        aiSidebar.style.width = `${Math.max(320, availableForAI)}px`;
      } else {
        // Try to restore saved width
        const savedWidth = localStorage.getItem('aiSidebarWidth');
        if (savedWidth) {
          const maxWidth = windowWidth - explorerWidth - 600 - 1;
          const width = Math.min(parseInt(savedWidth), maxWidth);
          aiSidebar.style.width = `${Math.max(320, width)}px`;
        }
      }
    }, 150);
  });
  
  console.log('✅ Resizable layout initialized');
}